/**
 * @file ai生成的自定义看板
 * @author alfielv
 */

import React, { useEffect, useCallback, useState } from 'react';
import { message, Empty, Dialog, Button } from '@tencent/spaui';
import { Text, Frame, Flex } from '@tencent/odc';
import _ from 'lodash';
import { useModal } from '@tencent/adp-fe-hooks';
import { useHistory, useParams } from 'react-router-dom';
import Icon from '@tencent/od-icon';
import axios from 'axios';

import ResponsiveIframe from '@/business-components/responsive-iframe';

import aiDashboardStore from '../../stores/dashboard';
import { getAIDashboardList, IAIDashboardList, deleteAIDashboard } from '../../api/analysis';

const AIDashboard = (props: { curDashboardInfo: IAIDashboardList[0], refreshDashboardList: () => void }) => {
  const { curDashboardInfo, refreshDashboardList } = props;
  const { session_id: sessionId, board_id: boardId, board_url: boardUrl } = curDashboardInfo || {};

  const history = useHistory();

  const [createModalInstance, modalContextHolder] = useModal();

  // 内嵌页面内容
  const [content, setContent] = useState();
  // 展示页面操作项
  const [showActions, setShowActions] = useState(false);

  // 删除看板
  const handleDelete = useCallback((id: number) => {
    createModalInstance({
      component: Dialog,
      componentProps: {
        title: '确认删除看板',
        useNewUI: true,
        submitButtonProps: {
          shape: 'round',
        },
        cancelButtonProps: {
          shape: 'round',
        },
      },
      body: '看板删除后无法恢复，确认删除吗？',
      onConfirm: async () => {
        const res = await deleteAIDashboard({
          board_id: id,
        });
        if (res?.code === 0) {
          message.success('删除成功');
          refreshDashboardList?.();
        }
      },
      onConfirmError(errorMessage) {
        message.error(errorMessage);
      },
    });
  }, [createModalInstance, refreshDashboardList]);

  useEffect(() => {
    if (!boardUrl) {
      message.error('未找到看板地址');
      return;
    }
    axios.get(boardUrl, {
      responseType: 'text',
      headers: {
        Accept: 'text/html',  // 明确告诉服务器需要纯文本
      },
    })
      .then((res) => {
        const content = _.get(res, 'data', '');

        setContent(content);
      })
      .catch(() => {
        message.error('获取资源失败');
      });
  }, [boardUrl]);

  // 看板id改变，重新隐藏操作项
  useEffect(() => () => {
    setShowActions(false);
  }, [boardId]);

  return (
    <Frame className="tw-mt-[16px]">
      {modalContextHolder}
      <Frame className='tw-pt-[8px] tw-relative'>
        {showActions && sessionId && (
          <Frame className='tw-absolute tw-right-[28px] tw-top-[48px] tw-z-10 tw-text-[12px] tw-text-gray-08'>
            session_id: {sessionId || '-'}
          </Frame>
        )}
        {showActions && (
          <Flex className='tw-absolute tw-right-[20px] tw-top-[14px] tw-z-10' gap={4}>
            <Button
              displayType="link"
              shape='round'
              icon={
                <Icon name='edit-outlined' />
              }
              onClick={() => history.push(`/analysis/ai-agent/${sessionId}`)}
            >编辑看板</Button>
            <Button
              displayType="link"
              shape='round'
              icon={
                <Icon name='delete-outlined' />
              }
              onClick={() => handleDelete(boardId)}
            >删除看板</Button>
          </Flex>
        )}
        {!!content && (
          <ResponsiveIframe
            key={boardId}
            srcDoc={content ?? ''}
            onLoad={() => setShowActions(true)}
          />
        )}
      </Frame>
    </Frame>
  );
};
export default () => {
  const { id } = useParams() as any;
  const history = useHistory();

  const { aiDashboardList } = aiDashboardStore;

  const curDashboardInfo = _.find(aiDashboardList, ({ board_id }) => board_id === _.toNumber(id));

  const handleAIDashboardListRefresh = useCallback(async () => {
    const latestAIDashboardList: IAIDashboardList = await getAIDashboardList();
    aiDashboardStore.setAIDashboardList(latestAIDashboardList);
    // 重定向到其他看板
    if (latestAIDashboardList?.length > 0) {
      history.replace(`/analysis/ai-dashboard/${_.takeRight(latestAIDashboardList)[0].board_id}`);
    }
  }, [history]);

  if (_.isNil(curDashboardInfo)) {
    return (
      <Frame className="py-12 px-20" radius={12} bg="white" style={{ height: 'calc(100vh - var(--l-topnav-height) - 24px)' }}>
        <Empty
          title={
            <Text type="comment" as="strong">
              无权限查看该看板，请通过「分析 Agent」创建
            </Text>
          }
          className="h-full"
        />
      </Frame>
    );
  }

  return (
    <AIDashboard
      curDashboardInfo={curDashboardInfo}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      refreshDashboardList={handleAIDashboardListRefresh}
    />
  );
};
